import React from 'react';
import { Box, Card, CardContent, Typography, Stack, Chip, Fade, LinearProgress } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FlagIcon from '@mui/icons-material/Flag';
import { modernTheme, formatMoney, formatHours, iconBoxSx } from './theme';

const getKPIColor = (progress) => {
  if (progress >= 100) return '#16a34a';
  if (progress >= 75) return '#2563eb';
  if (progress >= 50) return '#ea580c';
  return '#dc2626';
};

const getKPIGradient = (progress) => {
  if (progress >= 100) return 'linear-gradient(135deg, #16a34a 0%, #059669 100%)';
  if (progress >= 75) return 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
  if (progress >= 50) return 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)';
  return 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
};

const KPIRing = ({ value, size = 100, thickness = 8 }) => {
  const normalizedValue = Math.min(Math.max(value, 0), 100);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (normalizedValue / 100) * circumference;
  const color = getKPIColor(normalizedValue);

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="#e2e8f0" strokeWidth={thickness}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{
            transition: 'stroke-dashoffset 1s ease-in-out, stroke 0.3s ease',
            filter: `drop-shadow(0 0 4px ${color}40)`,
          }}
        />
      </svg>
      <Box sx={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontWeight: 800, color, lineHeight: 1, fontSize: size * 0.22 }}>
          {Math.round(normalizedValue)}%
        </Typography>
      </Box>
    </Box>
  );
};

const EarningsKPICard = ({ userSummary }) => {
  const kpi = userSummary?.kpi || {};
  const progress = kpi.progress || 0;
  const actual = kpi.actual || 0;
  const target = kpi.target || 160;
  const remaining = kpi.remaining || 0;
  const kpiStatus = kpi.status;
  const kpiColor = getKPIColor(progress);

  return (
    <Fade in timeout={700}>
      <Card sx={{
        borderRadius: 3,
        border: `1px solid ${modernTheme.colors.gray[200]}`,
        boxShadow: modernTheme.shadows.md,
        transition: 'all 0.2s ease',
        background: kpiStatus === 'completed'
          ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
          : modernTheme.gradients.card,
        '&:hover': { boxShadow: modernTheme.shadows.lg }
      }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>

          {/* All-time Earnings */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={iconBoxSx(modernTheme.gradients.secondary, 44)}>
              <ShowChartIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box sx={{ ml: 1.5 }}>
              <Typography variant="subtitle1" sx={{ color: modernTheme.colors.gray[900], fontWeight: 700, lineHeight: 1.2 }}>
                Tổng thu nhập
              </Typography>
              <Typography variant="caption" sx={{ color: modernTheme.colors.gray[500], fontSize: '0.7rem' }}>
                Toàn bộ thời gian
              </Typography>
            </Box>
          </Box>

          <Typography variant="h4" sx={{
            color: modernTheme.colors.secondary, fontWeight: 800, mb: 0.5,
            fontSize: { xs: '1.5rem', md: '1.75rem' }
          }}>
            {userSummary ? formatMoney(userSummary.allTime.earnings) : '0 ₫'}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2.5 }}>
            <AccessTimeIcon sx={{ fontSize: 15, color: modernTheme.colors.gray[400] }} />
            <Typography variant="caption" sx={{ color: modernTheme.colors.gray[500], fontWeight: 600 }}>
              {userSummary ? `${formatHours(userSummary.allTime.hours)}h tổng cộng` : '0h'}
            </Typography>
          </Stack>

          {/* KPI Section */}
          <Box sx={{
            p: 2, borderRadius: 2.5,
            bgcolor: modernTheme.colors.gray[50],
            border: `1px solid ${modernTheme.colors.gray[200]}`,
          }}>
            {/* KPI Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: '8px',
                  background: getKPIGradient(progress),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FlagIcon sx={{ color: 'white', fontSize: 16 }} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: modernTheme.colors.gray[800] }}>
                  KPI kỳ này
                </Typography>
              </Stack>
              <Chip
                label={kpiStatus === 'completed' ? 'Hoàn thành!' : kpiStatus === 'on-track' ? 'Đang tốt' : 'Cố lên!'}
                size="small"
                sx={{
                  height: 24, fontSize: '0.68rem', fontWeight: 700,
                  bgcolor: kpiColor, color: 'white',
                }}
              />
            </Stack>

            {/* Ring + Stats */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={2.5}
              sx={{ mb: 2 }}
            >
              <KPIRing value={progress} size={88} thickness={7} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" sx={{
                      color: modernTheme.colors.gray[500], fontWeight: 600,
                      fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>
                      Đã làm
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#16a34a', lineHeight: 1.2 }}>
                      {formatHours(actual)}h
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{
                      color: modernTheme.colors.gray[500], fontWeight: 600,
                      fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>
                      Mục tiêu
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: modernTheme.colors.gray[700], lineHeight: 1.2 }}>
                      {formatHours(target, 0)}h
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>

            {/* Progress Bar */}
            <Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, progress)}
                sx={{
                  height: 8, borderRadius: 4,
                  bgcolor: modernTheme.colors.gray[200],
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: getKPIGradient(progress),
                    transition: 'transform 0.8s ease',
                  },
                }}
              />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#16a34a', fontWeight: 700 }}>
                  {formatHours(actual)}h đã làm
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: modernTheme.colors.primary, fontWeight: 700 }}>
                  {remaining > 0 ? `${formatHours(remaining)}h còn lại` : 'Đạt KPI!'}
                </Typography>
              </Stack>
            </Box>
          </Box>

        </CardContent>
      </Card>
    </Fade>
  );
};

export default EarningsKPICard;
